import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import axios, { AxiosRequestConfig } from 'axios';
import { environment } from '../../../src/environments/environment';
import { StorageService } from 'app/core/auth/storage.service';
import { ModalService } from 'app/core/modal/modal.service';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private hostname: string;

    public fileName = '';
    public token = new BehaviorSubject<any>({});
    public userData = new BehaviorSubject<any>({});

    constructor(
        private http: HttpClient,
        private router: Router,
        private storage: StorageService,
        private modalService: ModalService
    ) {
        // const url = window.location.href.split('/').pop()?.split('?')[0];
        // const url = 'https://dusbam.ezgate.co.id/';
        this.hostname = environment.baseurl_api + environment.path;
    }

    /** ===================== 🔹 HEADER BUILDER 🔹 ===================== */
    private async getHeaders(
        auth: boolean = true
    ): Promise<AxiosRequestConfig> {
        const headers: any = {};
        if (auth) {
            const tokenExpiredStr = this.storage.getexp()?.replace(/"/g, '');
            const token = this.storage.geta()?.replace(/"/g, '');
            const clientSecret = this.getClientSecret();
            const now = Date.now();

            if (tokenExpiredStr) {
                const tokenExpired = parseInt(tokenExpiredStr, 10);

                if (now >= tokenExpired - 600000) {
                    try {
                        const refreshResponse = await this.refreshToken();
                        if (refreshResponse?.success) {
                            const newToken = refreshResponse.data.access_token;
                            const expiresIn = refreshResponse.data.expires_in;
                            const expiredAt = now + expiresIn * 1000;

                            localStorage.setItem(
                                'a_id',
                                this.storage.encryptData(newToken)
                            );
                            localStorage.setItem(
                                'a_exp',
                                this.storage.encryptData(expiredAt)
                            );

                            headers['Authorization'] = 'Bearer ' + newToken;
                            if (clientSecret) {
                                headers['ClientSecret'] = clientSecret;
                            }

                            this.startTokenScheduler(expiresIn);

                            console.log(
                                '[REFRESH] token refreshed via getHeaders()'
                            );
                        } else {
                            this.signOut();
                            throw new Error(
                                'Session expired, please login again.'
                            );
                        }
                    } catch (e) {
                        this.signOut();
                        throw e;
                    }
                } else if (token) {
                    headers['Authorization'] = 'Bearer ' + token;
                    if (clientSecret) {
                        headers['ClientSecret'] = clientSecret;
                    }
                }
            }
        }

        const tenant = this.storage.getTenant();
        if (tenant?.id) {
            headers['X-Tenant-ID'] = tenant.tenant_id;
        }

        // Add X-Cashier header for web self-order
        const xCashier = sessionStorage.getItem('x_cashier');
        if (xCashier) {
            headers['X-Cashier'] = xCashier;
        }

        return { headers };
    }

    private getClientSecret(): string | null {
        const clientSecretEnc = localStorage.getItem('c_secret');
        if (clientSecretEnc) {
            try {
                return this.storage.decryptionAES(clientSecretEnc)?.replace(/"/g, '');
            } catch (e) {
                return null;
            }
        }
        return null;
    }

    /** ===================== 🔹 MAIN METHODS 🔹 ===================== */
    async get(url: string, auth: boolean = true) {
        // console.log(this.hostname + url);
        try {
            const res = await axios.get(
                this.hostname + url,
                await this.getHeaders(auth)
            );
            return this.checkResponse(res.data);
        } catch (err: any) {
            return this.handleError(err);
        }
    }

    async post(url: string, data: any, auth: boolean = true) {
        try {
            console.log(this.hostname + url, data);
            let headers = {};
            if (url !== environment.auth) {
                headers = await this.getHeaders(auth);
            }
            const res = await axios.post(this.hostname + url, data, headers);
            // if (url === environment.auth && res.data?.data?.access_token) {

            //     const keys = this.storage.key(
            //         JSON.stringify(res.data.data.access_token)
            //     );
            //     localStorage.setItem('s_id', keys);
            //     localStorage.setItem(
            //         'a_id',
            //         this.storage.encryptData(res.data.data.access_token)
            //     );
            // }
            return this.checkResponse(res.data);
        } catch (err: any) {
            return this.handleError(err);
        }
    }

    // Payment API with separate base URL
    async postPayment(url: string, data: any, clientSecret?: string) {
        try {
            const paymentBaseUrl = environment.payment_api?.baseUrl || 'https://payment.adhivasindo.co.id/';
            const secret = clientSecret || environment.payment_api?.clientSecret || '';
            const formData = new URLSearchParams();
            Object.keys(data).forEach(key => {
                formData.append(key, data[key]);
            });
            
            const res = await axios.post(paymentBaseUrl + url, formData.toString(), {
                headers: {
                    'ClientSecret': secret,
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            return res.data;
        } catch (err: any) {
            console.error('Payment API Error:', err);
            return { success: false, error: err.message };
        }
    }

    async postFile(url: string, file: File, auth: boolean = true) {
        try {
            this.fileName = file.name;
            const formData = new FormData();
            formData.append('file', file);

            const res = await axios.post(this.hostname + url, formData, {
                ...(await this.getHeaders(auth)),
                headers: {
                    ...(await this.getHeaders(auth)).headers,
                    'Content-Type': 'multipart/form-data',
                },
            });

            return this.checkResponse(res.data);
        } catch (err: any) {
            return this.handleError(err);
        }
    }

    async postAuto(url: string, data: any, auth: boolean = true) {
        try {
            console.log(this.hostname + url, data);
            let headers = {};

            if (url !== environment.auth) {
                headers = await this.getHeaders(auth);
            }

            const isFormData = data instanceof FormData;
            const axiosConfig = isFormData
                ? {
                      ...(await this.getHeaders(auth)),
                      headers: {
                          ...(await this.getHeaders(auth)).headers,
                          'Content-Type': 'multipart/form-data',
                      },
                  }
                : headers;

            const res = await axios.post(
                this.hostname + url,
                data,
                axiosConfig
            );

            return this.checkResponse(res.data);
        } catch (err: any) {
            return this.handleError(err);
        }
    }

    async put(url: string, data: any, auth: boolean = true) {
        try {
            const res = await axios.put(
                this.hostname + url,
                data,
                await this.getHeaders(auth)
            );
            return this.checkResponse(res.data);
        } catch (err: any) {
            return this.handleError(err);
        }
    }

    async delete(url: string, id: string, auth: boolean = true) {
        try {
            const res = await axios.delete(
                `${this.hostname + url}/${id}`,
                await this.getHeaders(auth)
            );
            return this.checkResponse(res.data);
        } catch (err: any) {
            return this.handleError(err);
        }
    }

    async getById(url: string, id: string, auth: boolean = true) {
        return this.get(`${url}${id}`, auth);
    }

    async getAllById(
        url: string,
        column: string,
        id: string,
        auth: boolean = true
    ) {
        return this.get(`${column}/${url}/${id}`, auth);
    }

    async uploadFile(url: string, file: File, auth: boolean = true) {
        try {
            this.fileName = file.name;
            const formData = new FormData();
            formData.append('file', file);

            const res = await axios.post(this.hostname + url, formData, {
                ...(await this.getHeaders(auth)),
                headers: {
                    ...(await this.getHeaders(auth)).headers,
                    'Content-Type': 'multipart/form-data',
                },
            });

            return this.checkResponse(res.data);
        } catch (err: any) {
            return this.handleError(err);
        }
    }

    /** ===================== 🔹 RESPONSE HANDLER 🔹 ===================== */
    private checkResponse(response: any) {
        if (response?.success && response?.data) {
            return response;
        }
        if (Array.isArray(response)) {
            return response;
        }
        if (
            response?.message?.toLowerCase().includes('token expired') &&
            response?.success === false
        ) {
            this.modalService.alert(
                'Sesi Anda telah berakhir. Silakan login kembali.',
                'warning'
            );

            this.router.navigate(['/sign-out'], { replaceUrl: true });
            return;
        }
        return response;
    }

    private handleError(error: any) {
        if (error?.code === 'ERR_NETWORK') {
            this.noInternet();
        } else {
            return this.checkResponse(
                error?.response?.data || {
                    success: false,
                    message: 'Unknown Error',
                }
            );
        }
    }

    private noInternet() {
        // this.router.navigate(['/no-internet'], { replaceUrl: true });
        console.error('No Internet Connection');
    }

    /** ===================== 🔹 AUTH & SESSION 🔹 ===================== */

    private refreshTimer: any;

    public startTokenScheduler(expiresIn: number) {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
        const refreshTime = expiresIn * 1000 - 600000;
        console.log(
            `[SCHEDULER] Next refresh scheduled at: ${new Date(
                Date.now() + refreshTime
            ).toLocaleString()}`
        );

        this.refreshTimer = setTimeout(async () => {
            try {
                const refreshRes = await this.refreshToken();
                if (refreshRes?.success) {
                    const newToken = refreshRes.data.access_token;
                    const newExpiresIn = refreshRes.data.expires_in;
                    const expiredAt = Date.now() + newExpiresIn * 1000;

                    localStorage.setItem(
                        'a_id',
                        this.storage.encryptData(newToken)
                    );
                    localStorage.setItem(
                        'a_exp',
                        this.storage.encryptData(expiredAt)
                    );

                    console.log('[REFRESH] token refreshed via scheduler');

                    this.startTokenScheduler(newExpiresIn);
                } else {
                    this.signOut();
                }
            } catch (e) {
                this.signOut();
            }
        }, refreshTime);
    }

    refreshToken(): Promise<any> {
        console.log('[REFRESH] Trying refresh at', new Date().toISOString());
        return this.post(environment.refresh_token, {}, false);
    }

    public signOut() {
        localStorage.clear();
        this.router.navigate(['/sign-in'], { replaceUrl: true });
    }

    async isLoggedIn() {
        return this.storage.getuser();
    }
}
